import { inject, Injectable } from '@angular/core';
import { uniq } from 'lodash-es';
import { forkJoin, map, of } from 'rxjs';

import {
  ActivityType,
  GqlService,
  UpdateAccrualsInput,
  updateAccrualsMutation,
} from '@shared/services/gql.service';
import { Maybe } from '@shared/utils/utils';

import { QuarterCloseAdjustmentGridData } from '../quarter-close-adjustments.model';

type GroupedActivitiesByVendor = Record<
  string,
  { activities: QuarterCloseAdjustmentGridData[]; activityTypes: ActivityType[] }
>;

@Injectable()
export class AccrualsService {
  private readonly gqlService = inject(GqlService);

  private readonly maxActivityIdsBatchSize = 200;
  private readonly maxVendorsBatchSize = 50;

  batchUpdateAccruals$(
    groupedActivitiesByActivityTypeAndVendor: Map<string, QuarterCloseAdjustmentGridData[]>,
    activityIdsToSendForUpdate: Set<string>,
    period: string,
    manualActivityTypes?: Maybe<ActivityType[]>,
    notSendActivityIds?: Maybe<boolean>
  ) {
    const groupedVendors = this.groupVendorAndChangedActivityIds(
      groupedActivitiesByActivityTypeAndVendor,
      activityIdsToSendForUpdate
    );

    const inputs: UpdateAccrualsInput[] = [];

    const arrayOfVendors = Object.entries(groupedVendors);

    for (let i = 0; i < arrayOfVendors.length; i += this.maxVendorsBatchSize) {
      const vendorsBatch = arrayOfVendors.slice(i, i + this.maxVendorsBatchSize);

      const input: UpdateAccrualsInput = {
        accrual_inputs: [],
        period,
      };

      if (notSendActivityIds) {
        vendorsBatch.forEach(([vendor, { activityTypes }]) => {
          input.accrual_inputs.push({
            activity_ids: [],
            activity_types: manualActivityTypes || activityTypes,
            organization_id: vendor,
          });
        });

        inputs.push(input);

        continue;
      }

      vendorsBatch.forEach(([vendor, { activities, activityTypes }]) => {
        const activityIds = notSendActivityIds ? [] : activities;

        for (let j = 0; j < activityIds.length; j += this.maxActivityIdsBatchSize) {
          const activityIdsBatch = activityIds.slice(j, j + this.maxActivityIdsBatchSize);

          input.accrual_inputs.push({
            activity_ids: activityIdsBatch,
            activity_types: manualActivityTypes || activityTypes,
            organization_id: vendor,
          });
        }
      });

      inputs.push(input);
    }

    if (!inputs.length) {
      return of({ data: [], errors: [], success: true });
    }

    return forkJoin(inputs.map((input) => this.gqlService.updateAccruals$(input))).pipe(
      map(this.serializeBatchUpdateAccrualsResponse)
    );
  }

  /**
   * Batch update accruals by grouped vendors.
   *
   * @param groupedActivitiesByActivityTypeAndVendor - A map of activities grouped by activity type and vendor.
   * @param activityIdsToSendForUpdate - A set of activity IDs to send for update. This should always be provided to correctly identify what activity is related to a specific vendor. If you need to not send activity IDs, use the **notSendActivityIds** flag.
   * @param period - The period for which the accruals are being updated.
   * @param manualActivityTypes - Optional manual activity types to override the default activity types.
   * @param notSendActivityIds - Optional flag to indicate whether to send activity IDs or not.
   * @returns An observable of the batch update accruals response.
   */
  batchUpdateAccrualsByGroupedVendors$(
    groupedActivitiesByActivityTypeAndVendor: Map<string, QuarterCloseAdjustmentGridData[]>,
    activityIdsToSendForUpdate: Set<string>,
    period: string,
    manualActivityTypes?: ActivityType[],
    notSendActivityIds?: boolean
  ) {
    const groupedVendors = this.getAllEffectedActivitiesGroupedByVendor(
      groupedActivitiesByActivityTypeAndVendor,
      activityIdsToSendForUpdate
    );

    const inputs: UpdateAccrualsInput[] = [];

    const arrayOfVendors = Object.entries(groupedVendors);

    for (let i = 0; i < arrayOfVendors.length; i += this.maxVendorsBatchSize) {
      const vendorsBatch = arrayOfVendors.slice(i, i + this.maxVendorsBatchSize);

      const input: UpdateAccrualsInput = {
        accrual_inputs: [],
        period,
      };

      if (notSendActivityIds) {
        vendorsBatch.forEach(([vendor, { activityTypes }]) => {
          input.accrual_inputs.push({
            activity_ids: [],
            activity_types: manualActivityTypes || activityTypes,
            organization_id: vendor,
          });
        });

        inputs.push(input);

        continue;
      }

      vendorsBatch.forEach(([vendor, { activities, activityTypes }]) => {
        const activityIds = activities;

        for (let j = 0; j < activityIds.length; j += this.maxActivityIdsBatchSize) {
          const activityIdsBatch = activityIds
            .slice(j, j + this.maxActivityIdsBatchSize)
            .map((a) => a.activity_id);

          input.accrual_inputs.push({
            activity_ids: activityIdsBatch,
            activity_types: manualActivityTypes || activityTypes,
            organization_id: vendor,
          });
        }
      });

      inputs.push(input);
    }

    if (!inputs.length) {
      return of({ data: [], errors: [], success: true });
    }

    return forkJoin(inputs.map((input) => this.gqlService.updateAccruals$(input))).pipe(
      map(this.serializeBatchUpdateAccrualsResponse)
    );
  }

  getAllEffectedActivitiesGroupedByVendor(
    groupedActivitiesByActivityTypeAndVendor: Map<string, QuarterCloseAdjustmentGridData[]>,
    activityIdsToSendForUpdate: Set<string>
  ): GroupedActivitiesByVendor {
    const effectedVendors: [string, QuarterCloseAdjustmentGridData[], ActivityType][] = [];
    const activityTypes: ActivityType[] = [];

    groupedActivitiesByActivityTypeAndVendor.forEach((activities, key) => {
      const { activity: activityType, vendorName: effectedVendor } =
        this.parseGroupedActivitiesKey(key);

      const isServiceActivity = activityType === ActivityType.ACTIVITY_SERVICE;

      const isActivityAffected = activities.some((activity) =>
        activityIdsToSendForUpdate.has(activity.activity_id)
      );

      const alreadyAddedActivityType = activityType ? activityTypes.includes(activityType) : false;

      if (!isActivityAffected) {
        return;
      }

      if (!alreadyAddedActivityType && activityType) {
        activityTypes.push(activityType);
      }

      if (effectedVendor && activityType) {
        effectedVendors.push([effectedVendor, activities, activityType]);
      }

      if (!alreadyAddedActivityType && isServiceActivity) {
        const discountActivities = groupedActivitiesByActivityTypeAndVendor.get(
          `${ActivityType.ACTIVITY_DISCOUNT}_${effectedVendor}`
        );

        if (discountActivities?.length) {
          activityTypes.push(ActivityType.ACTIVITY_DISCOUNT);

          effectedVendors.push([
            effectedVendor,
            discountActivities || [],
            ActivityType.ACTIVITY_DISCOUNT,
          ]);
        }
      }
    });

    return effectedVendors.reduce<
      Record<
        string,
        { activities: QuarterCloseAdjustmentGridData[]; activityTypes: ActivityType[] }
      >
    >((accum, [vendor, activities, activityType]) => {
      const vendorData = accum[vendor];

      return {
        ...accum,
        [vendor]: {
          activities: uniq([...(vendorData?.activities || []), ...activities]),
          activityTypes: uniq([...(vendorData?.activityTypes || []), activityType]),
        },
      };
    }, {});
  }

  groupVendorAndChangedActivityIds(
    groupedActivitiesByActivityTypeAndVendor: Map<string, QuarterCloseAdjustmentGridData[]>,
    activityIdsToSendForUpdate: Set<string>
  ) {
    const groupedVendors: Record<string, { activities: string[]; activityTypes: ActivityType[] }> =
      {};

    groupedActivitiesByActivityTypeAndVendor.forEach((activities, key) => {
      const { activity: activityType, vendorName: effectedVendor } =
        this.parseGroupedActivitiesKey(key);

      const newActivities = activities
        .filter((activity) => activityIdsToSendForUpdate.has(activity.activity_id))
        .map((activity) => activity.activity_id);

      if (newActivities.length) {
        if (!groupedVendors[effectedVendor]) {
          groupedVendors[effectedVendor] = {
            activities: [],
            activityTypes: [],
          };
        }

        groupedVendors[effectedVendor].activities = [
          ...(groupedVendors[effectedVendor]?.activities || []),
          ...newActivities,
        ];

        groupedVendors[effectedVendor].activityTypes = uniq([
          ...(groupedVendors[effectedVendor]?.activityTypes || []),
          activityType!,
        ]);
      }
    });

    return groupedVendors;
  }

  private parseGroupedActivitiesKey(key: string): {
    activity: ActivityType | null;
    vendorName: string;
  } {
    let vendorName = '';
    let activity: ActivityType | null = null;

    [
      ActivityType.ACTIVITY_DISCOUNT,
      ActivityType.ACTIVITY_INVESTIGATOR,
      ActivityType.ACTIVITY_SERVICE,
      ActivityType.ACTIVITY_PASSTHROUGH,
    ].forEach((activityType) => {
      if (key.includes(activityType)) {
        activity = activityType;
        vendorName = key.replace(`${activityType}_`, '');
      }
    });

    return { activity, vendorName };
  }

  private serializeBatchUpdateAccrualsResponse(
    responses: GraphqlResponse<updateAccrualsMutation[]>[]
  ) {
    return responses.reduce(
      (acc, curr) => {
        return {
          data: curr.data ? [...(acc.data || []), ...curr.data] : acc.data,
          errors: curr.errors ? [...(acc.errors || []), ...curr.errors] : acc.errors,
          success: curr.success && acc.success,
        };
      },
      { data: [], errors: [], success: true }
    );
  }
}
